// Enhanced taskService.js with standardized implementation
import { 
  createService, 
  createFileUploadConfig, 
  createNestedCollection,
  createRelatedEntity  
} from './factory';
import { objectToQueryString } from '../utils/apiUtils';
import { api } from './api';

// Create the base task service using our factory
const taskServiceBase = createService('Task', '/tasks', {
  // File upload configuration for task attachments
  fileUploadOptions: createFileUploadConfig('file', 'attachments'),
  
  // Related entities (one-to-one relationships)
  relatedEntities: [
    createRelatedEntity('assignedTo'),
    createRelatedEntity('content'),
    createRelatedEntity('section', null, true)
  ],
  
  // Nested collections (one-to-many relationships)
  nestedCollections: [
    createNestedCollection('comments', 'comment'),
    createNestedCollection('attachments', 'attachment'),
    createNestedCollection('checklist', 'checklistItem'),
    createNestedCollection('subtasks', 'subtask', null, true)
  ]
});

// ========================
// Public API (backward compatible with existing taskService)
// This ensures all existing code continues to work with the new implementation
// ========================

// Get tasks with optional filters
export const getTasks = async (params = {}) => {
  console.log("🚀 ~ getTasks ~ params:", params)
  if(params.includeSections){
    delete params.includeSections;
  }
  return taskServiceBase.getAll(params);
};

// Get tasks for a specific project
export const getProjectTasks = async (projectId, params = {}) => {
  console.log("🚀 ~ getTasks ~ params:", params)
  if(params.includeSections){
    delete params.includeSections;
  }
  return taskServiceBase.getFromParent(projectId, '/projects', params);
};

// Get tasks assigned to the current user
export const getMyTasks = async (params = {}) => {
  const queryString = objectToQueryString(params);
  return api.get(`/tasks/me${queryString}`);
};

// Get a single task by ID
export const getTask = async (taskId) => {
  return taskServiceBase.getById(taskId);
};

// Create a new task
export const createTask = async (taskData) => {
  return taskServiceBase.create(taskData);
};

// Update an existing task (legacy PUT method)
export const updateTask = async (taskId, taskData) => {
  console.log('[taskService] Updating task:', taskId, 'with data:', taskData);
  const response = await taskServiceBase.update(taskId, taskData);
  console.log('[taskService] Update response:', response);
  return response;
};

// Unified PATCH update method - handles all types of updates
export const patchTask = async (taskId, updates) => {
  console.log('[taskService] Patching task:', taskId, 'with updates:', updates);
  const response = await api.patch(`/tasks/${taskId}`, updates);
  console.log('[taskService] Patch response:', response);
  return response;
};

// Reposition task (convenience method using patchTask)
export const repositionTask = async (taskId, toSectionId, position) => {
  console.log('[taskService] Repositioning task:', taskId, 'to section:', toSectionId, 'position:', position);
  return patchTask(taskId, {
    reposition: {
      toSectionId,
      position
    }
  });
};

// Delete a task
export const deleteTask = async (taskId) => {
  return taskServiceBase.delete(taskId);
};

// Create a task with content 
export const createTaskWithContent = async (data) => {
  return api.post('/tasks/content', data);
};

/**
 * Update task content stage
 * @param {string} taskId - Task ID
 * @param {string} stage - New content stage value
 * @returns {Promise} - Promise with response
 */
export const updateTaskContentStage = async (taskId, stage) => {
  return api.patch(`/tasks/${taskId}/content-stage`, { stage });
};

// Link content to a task
export const linkContentToTask = async (taskId, contentId) => {
  return api.post(`/tasks/${taskId}/content/${contentId}`);
};

// Get content details for a task
export const getTaskContent = async (taskId) => {
  return taskServiceBase.getContent(taskId);
};

// Update task status
export const updateTaskStatus = async (taskId, status) => {
  return taskServiceBase.updateStatus(taskId, status);
};

/**
 * Update a task's section
 * @param {string} taskId - Task ID
 * @param {Object} taskData - Updated task data
 * @param {string|null} sectionId - Section ID (null to remove from section)
 * @returns {Promise} - Promise with response
 */
export const updateTaskSection = async (taskId, taskData, sectionId) => {
  // Create the request data with the section
  const requestData = {
    ...taskData,
    sectionId: sectionId || null
  };
  
  const response = await taskServiceBase.update(taskId, requestData);
  return response.data;
};

/**
 * Move a task to a section
 * @param {string} taskId - Task ID
 * @param {string|null} sectionId - Section ID (null to remove from section)
 * @returns {Promise} - Promise with response
 */
export const moveTaskToSection = async (taskId, sectionId) => {
  const response = await api.patch(`/tasks/${taskId}/section`, { 
    sectionId: sectionId || null 
  });
  return response.data;
};

/**
 * Add a comment to a task
 * @param {string} taskId - Task ID
 * @param {string} text - Comment text
 * @param {boolean} isInternal - Whether the comment is internal
 * @param {Array} mentions - Array of user objects who were mentioned
 * @returns {Promise} - Promise with response
 */
export const addTaskComment = async (taskId, text, isInternal = false, mentions = []) => {
  const commentData = {
    text,
    isInternal,
    mentions: mentions.map(user => ({ userId: user._id }))
  };
  
  return api.post(`/tasks/${taskId}/comments`, commentData);
};

/**
 * Add a comment to a task (legacy method)
 * @param {string} taskId - Task ID
 * @param {Object} commentData - Comment data object
 * @returns {Promise} - Promise with response
 */
export const addTaskCommentLegacy = async (taskId, commentData) => {
  return taskServiceBase.addComment(taskId, commentData);
};

// Add a checklist item to a task
export const addTaskChecklistItem = async (taskId, itemData) => {
  return taskServiceBase.addChecklistItem(taskId, itemData);
};

// Toggle checklist item status
export const toggleTaskChecklistItem = async (taskId, itemId) => {
  return api.put(`/tasks/${taskId}/checklist/${itemId}`);
};

// Remove checklist item
export const removeTaskChecklistItem = async (taskId, itemId) => {
  return api.delete(`/tasks/${taskId}/checklist/${itemId}`);
};

// Upload a task attachment
export const uploadTaskAttachment = async (taskId, file, attachmentData = {}) => {
  return taskServiceBase.uploadFile(taskId, file, attachmentData);
};

// Get tasks by milestone
export const getTasksByMilestone = async (projectId, milestoneId) => {
  return api.get(`/projects/${projectId}/milestones/${milestoneId}/tasks`);
};

// Get subtasks for a task
export const getTaskSubtasks = async (taskId) => {
  return taskServiceBase.getSubtasks(taskId);
};

// Create a new subtask
export const createSubtask = async (taskId, subtaskData) => {
  return taskServiceBase.addSubtask(taskId, subtaskData);
};

// Reorder subtasks
export const reorderSubtasks = async (taskId, orderedIds) => {
  return taskServiceBase.reorderSubtasks(taskId, orderedIds);
};

// Remove a subtask
export const removeSubtask = async (taskId, subtaskId) => {
  return taskServiceBase.removeSubtask(taskId, subtaskId);
};

/**
 * Reorder tasks within a section
 * @param {string} sectionId - Section ID (null for tasks without section)
 * @param {Array} taskIds - Array of task IDs in desired order
 * @returns {Promise} - Promise with response
 */
export const reorderSectionTasks = async (sectionId, taskIds) => {
  // If sectionId is null, we're reordering tasks without a section
  const endpoint = sectionId 
    ? `/sections/${sectionId}/tasks/reorder`
    : `/tasks/reorder`;
  
  const response = await api.post(endpoint, { taskIds });
  return response.data;
};

/**
 * View As Feature - Get list of users that can be viewed
 * @returns {Promise} - Promise with response containing viewable users
 */
export const getMyTasksViewableUsers = async () => {
  return api.get('/tasks/mytasks-viewable-users');
};

/**
 * View As Feature - Get tasks for a specific user
 * @param {string} userId - User ID to view tasks as
 * @param {Object} params - Query parameters (groupByStatus, etc.)
 * @returns {Promise} - Promise with response containing tasks
 */
export const getMyTasksAsUser = async (userId, params = {}) => {
  const queryString = objectToQueryString(params);
  const url = `/tasks/mytasks/${userId}${queryString}`;
  console.log('getMyTasksAsUser: Calling API with URL:', url);
  console.log('getMyTasksAsUser: userId:', userId);
  console.log('getMyTasksAsUser: params:', params);
  return api.get(url);
};

/**
 * View As Feature - Update task order for a specific user
 * @param {string} userId - User ID whose task order is being updated
 * @param {string} taskId - Task ID to update
 * @param {Object} updates - Update data (newIndex, targetStatus, etc.)
 * @returns {Promise} - Promise with response
 */
export const updateMyTasksOrderAsUser = async (userId, taskId, updates) => {
  return api.patch(`/tasks/mytasks/${userId}/${taskId}`, updates);
};

// Export the base service for direct usage if needed
export const taskService = taskServiceBase;